"use client";

import { useState } from "react";
import type { DestinationsUi } from "@/lib/content/pages/destinations";
import { destinations } from "@/lib/destinations/data";
import GlobeInteractive from "@/components/destinations/GlobeInteractive";
import CityDetailPanel from "@/components/destinations/CityDetailPanel";
import DestinationsList from "@/components/destinations/DestinationsList";
import StickyStepsModal from "@/components/destinations/StickyStepsModal";

type Props = {
  locale: "fr" | "en";
  contactHref: string;
  ui: DestinationsUi;
};

/**
 * Composant client qui orchestre l'état partagé entre le globe, le panel et la liste
 * (toutes les interactions tournent autour d'un même `selectedId`).
 */
export default function DestinationsClient({ locale, contactHref, ui }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = destinations.find((d) => d.id === selectedId) ?? null;

  // Ville ouverte dans la pop-up "Sticky Steps" (via la liste du réseau)
  const [modalId, setModalId] = useState<string | null>(null);
  const modalDestination = destinations.find((d) => d.id === modalId) ?? null;

  return (
    <>
      {/* Zone interactive : globe + panel détail */}
      <section className="relative border-y border-white/5">
        {/* Fond bleu nuit avec léger dégradé radial pour évoquer l'espace */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, #0d2545 0%, #0a1a30 55%, #06101e 100%)",
          }}
        />
        <div className="relative h-[78vh] min-h-[560px] w-full">
          <GlobeInteractive
            destinations={destinations}
            selectedId={selectedId}
            onSelect={setSelectedId}
            locale={locale}
          />

          {/* Voile discret à gauche pour lisibilité de l'indicateur */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-black/20 to-transparent" />

          {/* Hint d'interaction */}
          {!selected && (
            <div className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.32em] uppercase text-white/55 font-body">
              {ui.globeHint}
            </div>
          )}

          {/* Panel détail ville */}
          <CityDetailPanel
            destination={selected}
            onClose={() => setSelectedId(null)}
            locale={locale}
            contactHref={contactHref}
            ui={ui}
          />
        </div>
      </section>

      {/* Liste alphabétique par région — clic = pop-up Sticky Steps */}
      <DestinationsList
        destinations={destinations}
        selectedId={modalId}
        onSelect={setModalId}
        locale={locale}
        ui={ui}
      />

      {/* Pop-up détail ville (Sticky Steps) */}
      <StickyStepsModal
        destination={modalDestination}
        onClose={() => setModalId(null)}
        locale={locale}
        contactHref={contactHref}
        ui={ui}
      />
    </>
  );
}
